import React, { useEffect, useState, useRef } from "react";
import { History, MapPin, Trophy, X, Swords, TrendingUp, Share2, Camera } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Avatar } from "./Avatar";
import { api } from "../lib/api";
import { formatDateLong, openWhatsappProfile } from "../lib/padel";
import { fmtScore } from "./RatingsDialog";
import { AdminPlayerActions } from "./AdminPlayerActions";
import { useAuth } from "../lib/auth";

const AWARD_ICONS = { destra: "👉", sinistra: "👈", sorpresa: "⚖️", leale: "🤝", disonesto: "🦊", professore: "🎓" };

const AwardsShelf = ({ awards }) => {
  if (!awards.length) return null;
  return (
    <div className="rounded-xl bg-[#ccff00]/8 border border-[#ccff00]/25 px-3 py-2.5" data-testid="awards-shelf">
      <p className="text-xs font-bold uppercase tracking-wider text-[#ccff00] mb-2 flex items-center gap-1.5">
        <Trophy className="w-3.5 h-3.5" /> Bacheca trofei
      </p>
      <div className="flex flex-wrap gap-2">
        {awards.map((a) => (
          <span
            key={`${a.year}-${a.category}`}
            title={`${a.label} ${a.year} · ${a.votes} voti`}
            data-testid={`award-badge-${a.year}-${a.category}`}
            className="inline-flex items-center gap-1.5 rounded-full bg-[#0b1420] border border-[#1e2c4a] px-2.5 py-1 text-xs text-slate-200"
          >
            🏆 {AWARD_ICONS[a.category]} {a.label} <span className="text-[#ccff00] font-bold">{a.year}</span>
          </span>
        ))}
      </div>
    </div>
  );
};

export const PlayerHistoryDialog = ({ player, allPlayers = [], currentName, onUploadPhoto, onClose, onAdminChanged }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [elo, setElo] = useState([]);
  const [awards, setAwards] = useState([]);
  const [ratingSum, setRatingSum] = useState(null);
  const [compareKey, setCompareKey] = useState("");
  const [compareElo, setCompareElo] = useState([]);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!player) return;
    setCompareKey("");
    setCompareElo([]);
    setLoading(true);
    Promise.all([
      api.playerMatches(player.name).then(setRows).catch(() => setRows([])),
      api.playerElo(player.name).then(setElo).catch(() => setElo([])),
      api.playerAwards(player.name).then(setAwards).catch(() => setAwards([])),
      api.ratingsSummary(player.name).then(setRatingSum).catch(() => setRatingSum(null)),
    ]).finally(() => setLoading(false));
  }, [player]);

  const compareWith = (key) => {
    setCompareKey(key);
    const target = allPlayers.find((p) => p.key === key);
    if (!target) { setCompareElo([]); return; }
    api.playerElo(target.name).then(setCompareElo).catch(() => setCompareElo([]));
  };

  if (!player) return null;

  const nemesis = (() => {
    const counts = {};
    rows.forEach((m) => {
      if (!m.won) m.opponents.forEach((o) => { counts[o] = (counts[o] || 0) + 1; });
    });
    const entries = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    return entries.length ? { name: entries[0][0], count: entries[0][1] } : null;
  })();

  const isMe = currentName && player.key === currentName.toLowerCase();
  const pickPhoto = (e) => {
    const f = e.target.files?.[0];
    if (f && onUploadPhoto) onUploadPhoto(player.name, f);
    e.target.value = "";
  };

  return (
    <Dialog open={!!player} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#121b2d] border-[#1e2c4a] text-slate-100 max-w-md max-h-[85vh] overflow-y-auto" data-testid="history-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl flex items-center gap-3">
            <div className="relative shrink-0">
              <Avatar name={player.name} photoPath={player.photo_path} size={44} />
              {isMe && (
                <>
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    data-testid="profile-upload-photo-btn"
                    title="Cambia foto"
                    className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-[#ccff00] text-[#090d16] flex items-center justify-center border-2 border-[#121b2d] hover:bg-[#aeea00]"
                  >
                    <Camera className="w-3 h-3" />
                  </button>
                  <input ref={fileRef} type="file" accept="image/*" onChange={pickPhoto} className="hidden" data-testid="profile-photo-input" />
                </>
              )}
            </div>
            <span className="truncate">{player.name}</span>
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {isMe ? "La tua scheda. Tocca la fotocamera per cambiare foto." : "Storico delle ultime partite giocate."}
          </DialogDescription>
        </DialogHeader>

        {isAdmin && (
          <div className="rounded-xl border border-[#ccff00]/25 bg-[#ccff00]/5 p-2.5" data-testid="profile-admin-actions">
            <p className="text-[10px] uppercase tracking-wider text-[#ccff00] font-bold mb-2">Strumenti amministratore</p>
            <AdminPlayerActions name={player.name} hasPhoto={!!player.photo_path} hidden={false}
              onChanged={(newName) => { onAdminChanged?.(); if (newName !== player.name) onClose(); }} />
          </div>
        )}

        <div className="grid grid-cols-3 gap-2 py-1">
          <Stat label="Punti" value={player.points} accent />
          <Stat label="ELO" value={player.elo ?? 1000} />
          <Stat label="Gioc." value={player.played} />
          <Stat label="Vittorie" value={player.wins} />
          <Stat label="% Vitt." value={`${player.win_rate}%`} />
          <Stat label="Record 🔥" value={player.best_streak ?? 0} />
        </div>

        <AwardsShelf awards={awards} />

        {ratingSum?.count > 0 && (
          <div className="grid grid-cols-2 gap-2" data-testid="profile-ratings">
            <Stat label="Voto medio" value={fmtScore(ratingSum.avg)} accent />
            <div className="rounded-xl bg-[#0b1420] border border-[#1e2c4a] px-3 py-2 text-center" data-testid="profile-best-rating">
              <p className="font-heading font-extrabold text-xl text-slate-100">{fmtScore(ratingSum.best.score)}</p>
              <p className="text-[10px] uppercase tracking-wider text-slate-500">Miglior voto</p>
              <p className="text-[10px] text-slate-400 truncate">da {ratingSum.best.rater}{ratingSum.best.venue ? ` · ${ratingSum.best.venue}` : ""}</p>
            </div>
          </div>
        )}

        <button
          type="button"
          onClick={() => openWhatsappProfile(player)}
          data-testid="share-profile-btn"
          className="w-full flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#20bd5a] text-white font-bold rounded-xl h-10 text-sm transition-colors"
        >
          <Share2 className="w-4 h-4" /> Condividi scheda su WhatsApp
        </button>

        {nemesis && (
          <div className="rounded-xl bg-[#ff4d4d]/8 border border-[#ff4d4d]/25 px-3 py-2.5 flex items-center gap-2" data-testid="nemesis-box">
            <Swords className="w-4 h-4 text-[#ff4d4d] shrink-0" />
            <p className="text-sm text-slate-200">
              <span className="text-[#ff4d4d] font-bold">Nemesi:</span> {nemesis.name}
              <span className="text-slate-400"> · {nemesis.count} {nemesis.count === 1 ? "sconfitta" : "sconfitte"}. Tempo di rivincita! 🔥</span>
            </p>
          </div>
        )}

        {elo.length > 1 && (
          <div data-testid="elo-chart">
            <div className="flex items-center justify-between gap-2 mb-2 mt-1">
              <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-400">
                <TrendingUp className="w-4 h-4" /> Andamento ELO
              </div>
              <Select value={compareKey || "none"} onValueChange={(v) => compareWith(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="elo-compare-select" className="h-8 w-40 bg-slate-900/60 border-slate-700 text-slate-200 text-xs">
                  <SelectValue placeholder="Confronta con..." />
                </SelectTrigger>
                <SelectContent className="bg-[#121b2d] border-[#1e2c4a] text-slate-100">
                  <SelectItem value="none">Nessun confronto</SelectItem>
                  {allPlayers.filter((p) => p.key !== player.key).map((p) => (
                    <SelectItem key={p.key} value={p.key}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="rounded-xl bg-slate-900/60 border border-slate-700 p-2 h-44">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={Array.from({ length: Math.max(elo.length, compareElo.length) }, (_, i) => ({
                    match: i, elo: elo[i]?.elo, eloB: compareElo[i]?.elo,
                  }))}
                  margin={{ top: 8, right: 10, left: -18, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e2c4a" />
                  <XAxis dataKey="match" tick={{ fill: "#64748b", fontSize: 10 }} stroke="#1e2c4a" />
                  <YAxis domain={["dataMin - 20", "dataMax + 20"]} tick={{ fill: "#64748b", fontSize: 10 }} stroke="#1e2c4a" width={44} />
                  <Tooltip
                    contentStyle={{ background: "#121b2d", border: "1px solid #1e2c4a", borderRadius: 12, color: "#f8fafc" }}
                    labelFormatter={(v) => v === 0 ? "Inizio" : `Partita ${v}`}
                  />
                  {compareKey && <Legend wrapperStyle={{ fontSize: 11 }} />}
                  <Line name={player.name} type="monotone" dataKey="elo" stroke="#ccff00" strokeWidth={2.5} dot={{ r: 3, fill: "#ccff00" }} activeDot={{ r: 5 }} connectNulls />
                  {compareKey && (
                    <Line name={allPlayers.find((p) => p.key === compareKey)?.name || "Avversario"} type="monotone" dataKey="eloB" stroke="#00e5ff" strokeWidth={2.5} dot={{ r: 3, fill: "#00e5ff" }} activeDot={{ r: 5 }} connectNulls />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-400 mt-1">
          <History className="w-4 h-4" /> Partite recenti
        </div>

        {loading ? (
          <p className="text-slate-500 text-sm py-6 text-center">Caricamento...</p>
        ) : rows.length === 0 ? (
          <p className="text-slate-500 text-sm py-6 text-center">Nessuna partita giocata con risultato.</p>
        ) : (
          <div className="space-y-2">
            {rows.map((m) => (
              <div
                key={m.match_id}
                data-testid={`history-row-${m.match_id}`}
                className={`rounded-xl border px-3 py-2.5 ${m.won ? "bg-[#ccff00]/8 border-[#ccff00]/25" : "bg-slate-900/50 border-slate-700/60"}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className={`text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${m.won ? "bg-[#ccff00] text-[#090d16]" : "bg-[#ff4d4d]/20 text-[#ff4d4d]"}`}>
                    {m.won ? "Vittoria" : "Sconfitta"}
                  </span>
                  <span className="font-heading font-bold text-sm text-slate-200">{m.score}</span>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-slate-400 mt-1.5">
                  <MapPin className="w-3 h-3" /> {m.venue} · {formatDateLong(m.date)}
                </div>
                <div className="text-xs text-slate-400 mt-1">
                  {m.partner && <span>con <b className="text-slate-200">{m.partner}</b> </span>}
                  <span>vs {m.opponents.join(" & ")}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Stat = ({ label, value, accent }) => (
  <div className="rounded-xl bg-slate-900/60 border border-slate-700 px-2 py-2 text-center">
    <p className={`font-heading font-extrabold text-lg leading-none ${accent ? "text-[#ccff00]" : "text-slate-100"}`}>{value}</p>
    <p className="text-[10px] uppercase tracking-wider text-slate-500 mt-1">{label}</p>
  </div>
);
